import React, { Component } from 'react';
import { Redirect } from 'react-router';
import { connect } from 'react-redux';

import { getApplication, setStep, updateApplication, submitApplication } from '../actions';
import { sanitizeBasicInfo, sanitizeEducationInfo, sanitizeOtherInfo, sanitizeProfessionalInfo, sanitizePTRequirementsInfo } from "../libs/utilities";
import { applicationStatuses, statusTypes, PROGRAM_ADN, PROGRAM_PT, DEBUG } from '../config/constants';
import { BasicValidation } from '../validation/basicValidation';
import { EducationValidation, EducationValidationPT } from "../validation/educationValidation";
import { ProfessionalValidation } from "../validation/professionalValidation";
import { OtherValidation } from "../validation/otherValidation";
import { PTRequirementsValidation } from "../validation/ptRequirementsValidation";
import BasicInfo from './BasicInfo';
import ProfessionalInfo from './ProfessionalInfo';
import OtherInfo from './OtherInfo';
import EducationInfo from './EducationInfo';
import PTRequirements from './PTRequirements';
import FormSummary from './FormSummary';
import FormNav from './FormNav';
import FormButtonToolbar from './FormButtonToolbar';

class ApplicationForm extends Component {
  constructor(props) {
    super(props);
    this.previousStep = this.previousStep.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.handleStepChange = this.handleStepChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.getCurrentFormComponent = this.getCurrentFormComponent.bind(this);
  }

  componentDidMount(){
    // Fetching application fails on hard refresh because authentication may not be complete
    // As a work around we bump the student back to the applications page for now.
    if(this.props.userSet)
      this.props.dispatch(getApplication(this.props.deltaId, this.props.match.params.applicationId));
    else
      this.props.history.push('/');
  }

  nextStep() {
    const newStep = this.props.step + 1;
    this.props.dispatch(setStep(newStep));
    if(this.props.applicationUpdated) {
      const { basicInfo, educationInfo, otherInfo, professionalInfo, upgradeOption, ptRequirementsInfo, programId } = this.props;
      const updates = {
        basic: sanitizeBasicInfo(basicInfo),
        education: sanitizeEducationInfo(educationInfo)
      };
      if(programId === PROGRAM_ADN) {
        updates.other = sanitizeOtherInfo(otherInfo);
        updates.professional = sanitizeProfessionalInfo(professionalInfo);
        updates.upgradeOption = upgradeOption;
      } else if(programId === PROGRAM_PT) {
        updates.ptRequirements = sanitizePTRequirementsInfo(ptRequirementsInfo);
      }
      this.props.dispatch(updateApplication(updates));
    }
  }

  previousStep() {
    const newStep = this.props.step - 1;
    this.props.dispatch(setStep(newStep));
  }

  handleSubmit() {
    const { basicInfo, educationInfo, otherInfo, professionalInfo, upgradeOption, ptRequirementsInfo, programId } = this.props;
    const application = {
      basic: sanitizeBasicInfo(basicInfo),
      education: sanitizeEducationInfo(educationInfo),
      status: applicationStatuses.PENDING.code
    };
    if(programId === PROGRAM_ADN) {
      application.other = sanitizeOtherInfo(otherInfo);
      application.professional = sanitizeProfessionalInfo(professionalInfo);
      application.upgradeOption = upgradeOption;
    } else if(programId === PROGRAM_PT) {
      application.ptRequirements = sanitizePTRequirementsInfo(ptRequirementsInfo)
    }
    this.props.dispatch(submitApplication(application));
    window.scrollTo(0, 0);
  }

  handleStepChange(step) {
    if (step !== this.props.step)
      this.props.dispatch(setStep(step));
  }

  canSubmit(){
    const { programId } = this.props;
    for(let confirmation of this.props.confirmations) {
      if(!confirmation.status) {
        //if(DEBUG)
        //  console.log('CAN SUBMIT', 'Failed Confirmations');
        return false;
      }
    }

    // Universal Requirements
    if(!(new BasicValidation().getValidationStatus(this.props.basicInfo).section.status === statusTypes.SUCCESS)) {
      //if(DEBUG)
      //  console.log('CAN SUBMIT','Failed Basic Info');
      return false;
    }

    // ADN Requirements
    if(programId === PROGRAM_ADN && !(new EducationValidation().getValidationStatus(this.props.educationInfo).section.status === statusTypes.SUCCESS)) {
      //if(DEBUG)
      //  console.log('CAN SUBMIT-Failed ADN Education');
      return false;
    }
    if(programId === PROGRAM_ADN && !(new ProfessionalValidation(this.props.educationInfo.colleges).getValidationStatus(this.props.professionalInfo).section.status === statusTypes.SUCCESS)) {
      //if(DEBUG)
      //  console.log('CAN SUBMIT-Failed ADN Professional');
      return false;
    }
    if(programId === PROGRAM_ADN && !(new OtherValidation().getValidationStatus(this.props.otherInfo).section.status === statusTypes.SUCCESS)) {
      //if(DEBUG)
      //  console.log('CAN SUBMIT-Failed ADN Other');
      return false;
    }

    // PT Requirements
    if(programId === PROGRAM_PT && !(new EducationValidationPT().getValidationStatus(this.props.educationInfo).section.status === statusTypes.SUCCESS)) {
      //if(DEBUG)
      //  console.log('Failed PT Education');
      return false;
    }
    return !(programId === PROGRAM_PT && !(new PTRequirementsValidation().getValidationStatus(this.props.ptRequirementsInfo).section.status === statusTypes.SUCCESS));

  }

  getCurrentFormComponent() {
    let applicationId = this.props.match.params.applicationId;
    const { programId, step } = this.props;
    // If the form is accessed without an applicationId, we need to get one
    if(typeof applicationId === 'undefined')
      return <Redirect to="/information/"/>;
    // If the application has already been submitted, we can't update it
    if(this.props.submitted)
      return <Redirect to={'/application/'+applicationId}/>;
    // Some programs don't require all steps
    switch (true) {
      case (((programId === PROGRAM_ADN) || (programId === PROGRAM_PT)) && (step === 1)):
        return (
          <BasicInfo/>
        );
      case (((programId === PROGRAM_ADN) || (programId === PROGRAM_PT)) && (step === 2)):
        return (
          <EducationInfo/>
        );
      case ((programId === PROGRAM_PT) && (step === 3)):
        return (
          <PTRequirements/>
        );
      case ((programId === PROGRAM_ADN) && (step === 3)):
        return (
          <ProfessionalInfo/>
        );
      case ((programId === PROGRAM_ADN) && (step === 4)):
        return (
          <OtherInfo/>
        );
      default:
        return (
          <FormSummary/>
        )
    }
  };

  render() {
    return this.props.applicationSet ?
      (
        <div>
          {!this.props.submitted &&
          <FormNav onStepChange={this.handleStepChange}/>
          }
          {this.getCurrentFormComponent()}
          <FormButtonToolbar step={this.props.step}
                             maxStep={this.props.maxStep}
                             nextStep={this.nextStep}
                             previousStep={this.previousStep}
                             applicationUpdated={this.props.applicationUpdated}
                             onSubmit={this.handleSubmit}
                             canSubmit={this.canSubmit()}/>
        </div>
     )
    :
     (
       <div>
         Please wait while we load your application.
       </div>
     )
  };
}

function mapStateToProps(state) {
  const { auth, appInfo, form, basicInfo, educationInfo, otherInfo, professionalInfo, ptRequirementsInfo } = state;
  const { confirmations, upgradeOption } = state.application;
  const { step, maxStep, submitted } = form;
  const { userSet, user } = auth;
  const { applicationSet, applicationUpdated, applicationPeriods, application } = appInfo;
  const { programId } = appInfo.application;
  const currentPeriod = applicationSet ? applicationPeriods.find(appPeriod => appPeriod.periodId === application.periodId) : {};
  return {
    basicInfo,
    educationInfo,
    otherInfo,
    professionalInfo,
    ptRequirementsInfo,
    confirmations,
    upgradeOption,
    programId,
    applicationUpdated,
    applicationSet,
    appInfo,
    userSet,
    deltaId: user ? user.deltaId : '',
    step,
    maxStep,
    currentPeriod,
    submitted
  }
}

export default connect(mapStateToProps)(ApplicationForm);